import React from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, IconButton, InputLabel,
    MenuItem, Select, TextField, Tooltip, Typography
} from '@material-ui/core/';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import RestoreIcon from '@material-ui/icons/Restore';
import AddIcon from '@material-ui/icons/Add';

import EnhancedTable from "@apricityhealth/web-common-lib/components/EnhancedTable";
import User from '@apricityhealth/web-common-lib/components/User';
import Config from '@apricityhealth/web-common-lib/Config';
import Axios from '@apricityhealth/web-common-lib/utils/Axios';
import getErrorMessage from '@apricityhealth/web-common-lib/utils/getErrorMessage';

import { OPTION_TYPES, optionType } from './algorithmOptions';
import Moment from 'moment';
import { Diff2Html } from 'diff2html';
import { createTwoFilesPatch } from 'diff';
import 'diff2html/dist/diff2html.min.css';

// `prism-core` rather than `prismjs`: the full bundle runs highlightAll() on DOMContentLoaded and would go
// looking for elements elsewhere on the page. The core exports the same Prism object without that side effect,
// and each grammar below registers itself onto it.
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs/components/prism-core';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';

const DATE_FORMAT = 'YYYY-MM-DD HH:mm';

/**
 * The manifest is not an ordinary file — it is what makes the folder an algorithm at all.
 *
 * So it is not shown as one. It is hidden from every file list and edited through the properties form below,
 * because nobody should be hand-writing JSON to say what an algorithm is called. Exported because the tree
 * hides it from its file nodes for the same reason.
 */
export const MANIFEST_FILE = '.manifest.json';

/** What it was called before the rename. Still filtered out of file lists wherever it survives. */
export const LEGACY_MANIFEST_FILE = 'manifest.json';

/** Neither name is a file anyone should be editing as one. */
export const isManifestFile = (name) => name === MANIFEST_FILE || name === LEGACY_MANIFEST_FILE;

/** The manifest keys the form owns. Anything else is round-tripped untouched — see `manifestToSave`. */
const FORM_KEYS = ['name', 'description', 'script', 'dependencies', 'opts', 'deploy', 'resultFormat'];

/**
 * Which generic views a result can feed — the same vocabulary the service keeps in `RESULT_FORMATS`.
 *
 * Declared on the manifest rather than printed by the script: the service copies it onto the job when the run
 * finishes, so a view can find renderable jobs without reading every result out of S3. Each one is a contract
 * on the result document, which is what the second line of each label is for — picking `curve` here and never
 * emitting a `curves` key produces a job that lists in the Curves view and draws nothing.
 */
const RESULT_FORMATS = [
    { id: 'clustering', label: 'Clustering', hint: 'clusters, cluster_centroids — the 3D scene' },
    { id: 'pca', label: 'PCA', hint: 'components, patients_transformed — the projection' },
    { id: 'curve', label: 'Curve', hint: 'curves: [{ title, xLabel, yLabel, series }]' },
    { id: 'heatmap', label: 'Heatmap', hint: 'heatmaps: [{ columns, rows, values }]' },
    { id: 'table', label: 'Table', hint: 'tables: [{ title, rows: [{…}] }]' },
    { id: 'metrics', label: 'Metrics', hint: 'metrics: { name: number }' }
];

/** A code box, not a paragraph box. */
const CODE_STYLE = {
    fontFamily: 'Consolas, Menlo, monospace',
    fontSize: 13,
    whiteSpace: 'pre',
    overflowX: 'auto'
};

/**
 * Which Prism grammar an algorithm file gets, by extension.
 *
 * Only the two that actually occur. Anything else — a .txt, a requirements file, a folder's stray README —
 * falls through to escaped plain text rather than being highlighted as something it is not.
 */
const GRAMMARS = { py: 'python', json: 'json' };

const escapeHtml = (code) => String(code)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Highlight one file's contents for the editor.
 *
 * Falls back to escaped text on an unknown extension **and on a Prism failure**: this runs on every keystroke,
 * and a grammar that throws half-way through an edit would blank the editor rather than fail visibly. Escaping
 * is not optional in either branch — the output is injected as HTML.
 */
function highlightCode(code, fileName) {
    const language = GRAMMARS[String(fileName || '').split('.').pop().toLowerCase()];
    const grammar = language && Prism.languages[language];
    if (!grammar) return escapeHtml(code);
    try {
        return Prism.highlight(code, grammar, language);
    } catch (err) {
        return escapeHtml(code);
    }
}

/** Matches the outlined TextField this replaced, so the editor still looks like the rest of the form. */
const CODE_EDITOR_STYLE = {
    ...CODE_STYLE,
    whiteSpace: 'pre-wrap',         // see renderCode: the pre and the textarea have to agree, and pre-wrap is
    overflowX: 'visible',           // what the component sets on both of them
    lineHeight: 1.5,
    minHeight: 430,                 // ≈ the 22 rows the TextField showed at this size
    maxHeight: 780,                 // ≈ 40 rows, after which it scrolls
    overflowY: 'auto',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    borderRadius: 4,
    backgroundColor: '#fff'
};

/**
 * What `pip install` will accept.
 *
 * Deliberately the same expression the service uses (`DEPENDENCY_RE` in apricity-ml.js). Duplicated on purpose:
 * without it, the only feedback for a typo is a Batch job that fails much later, with pip's error rather than
 * one that says which manifest was wrong.
 */
const DEPENDENCY_RE = /^[A-Za-z0-9][A-Za-z0-9._-]*(\[[A-Za-z0-9,._-]+\])?((==|>=|<=|~=|!=|>|<)[A-Za-z0-9._*+!-]+)?$/;

/**
 * Edit one algorithm.
 *
 * Two modes, chosen by whether a file was asked for:
 *
 * - **no `openFile`** — the algorithm's own properties: name, description, entry script, pip packages and the
 *   options it accepts. This is the manifest, as a form.
 * - **an `openFile`** — that file's code, in a plain monospace editor.
 *
 * **The form preserves manifest keys it does not know about.** `kmeans`, `knn`, `pca` and `kmeans-generative`
 * each carry a `deploy` block that nothing here understands, and a form saving only its own fields would
 * silently delete it. Unknown keys are round-tripped — and surfaced under Advanced, because a preserved key
 * nobody can see is a key nobody can fix.
 */
export class MLAlgorithmView extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            algorithm: null,        // manifest + files, from the server
            raw: null,              // the manifest as stored, so unknown keys survive a save
            form: null,             // the editable subset
            advanced: '',           // unknown keys, as JSON
            // `deploy.input`/`deploy.output` are objects, but a form has to edit them as ordered rows —
            // re-keying an object on every keystroke loses focus and mangles half-typed names. Folded back
            // into the manifest by `manifestToSave`, which stays the single description of what gets written.
            deployInput: [],        // [{ key, value }]
            deployOutput: [],
            body: '',               // file mode: the code
            original: '',           // what a save would produce right now, for the dirty check and Revert
            note: '',
            // The name the server actually read. It resolves a manifest request to whichever name the folder
            // uses, so this is not always what was asked for.
            actualFile: '',
            history: [],
            diff: '',
            confirm: null,          // { title, body, label, onConfirm }
            progress: null,
            error: null
        };
    }

    componentDidMount() {
        this.loadAlgorithm();
    }

    /**
     * Reload when the list points at something else.
     *
     * The drawer keeps this component mounted and only swaps props, so `componentDidMount` does not run again.
     * `openFile` is compared as well as the ref: moving between the properties form and a file, or between two
     * files of the same algorithm, changes only that prop.
     */
    componentDidUpdate(prevProps) {
        const was = prevProps.algorithm && (prevProps.algorithm.ref || prevProps.algorithm.name);
        if (was !== this.ref || prevProps.openFile !== this.props.openFile) {
            this.setState({
                algorithm: null, raw: null, form: null, advanced: '', body: '', original: '',
                note: '', history: [], diff: '', error: null
            }, this.loadAlgorithm.bind(this));
        }
    }

    get ref() {
        const { algorithm } = this.props;
        return algorithm && (algorithm.ref || algorithm.name);
    }

    /** The file this editor is looking at — a real file, or the manifest behind the properties form. */
    get file() {
        return this.props.openFile || MANIFEST_FILE;
    }

    /**
     * A plain folder has no manifest, so there is nothing to show but a file.
     *
     * The tree offers no Edit action for one, so this should not arise — but loading properties for a folder
     * would try to read a `.manifest.json` that does not exist and report it as an error, which is a confusing
     * way to say "this is a folder".
     */
    get isFolder() {
        const { algorithm } = this.state;
        return !!(algorithm && algorithm.isFolder);
    }

    get editingFile() {
        return !!this.props.openFile;
    }

    get readOnly() {
        const { algorithm } = this.state;
        return !algorithm || algorithm.access !== 'write';
    }

    /** The manifest as it would be saved: the form's fields over everything that was already there. */
    manifestToSave() {
        const { raw, form, advanced, deployInput, deployOutput } = this.state;
        // Throws on invalid JSON, deliberately — the caller decides whether that is an error to show or a
        // reason to treat the form as dirty.
        const extra = (advanced && advanced.trim()) ? JSON.parse(advanced) : {};
        const merged = { ...(raw || {}), ...extra, ...form };

        // Rows back to objects. A row with no option name is a half-finished edit, not a mapping.
        const obj = (list) => (list || []).reduce((o, r) => {
            if (r.key && r.key.trim()) o[r.key.trim()] = r.value;
            return o;
        }, {});
        const input = obj(deployInput);
        const output = obj(deployOutput);
        if (Object.keys(input).length || Object.keys(output).length) {
            merged.deploy = {
                ...(Object.keys(input).length ? { input } : {}),
                ...(Object.keys(output).length ? { output } : {})
            };
        } else {
            delete merged.deploy;
        }

        // An empty list is noise in the file; absent says the same thing.
        if (!merged.dependencies || !merged.dependencies.length) delete merged.dependencies;
        if (!merged.opts || !merged.opts.length) delete merged.opts;
        if (!merged.resultFormat || !merged.resultFormat.length) delete merged.resultFormat;
        return merged;
    }

    get dirty() {
        if (this.editingFile) return this.state.body !== this.state.original;
        if (!this.state.form) return false;
        try {
            return JSON.stringify(this.manifestToSave(), null, 4) !== this.state.original;
        } catch (err) {
            return true;        // unparseable Advanced JSON counts as an unsaved change
        }
    }

    notifyDirty() {
        if (this.props.onDirtyChange) this.props.onDirtyChange(this.dirty);
    }

    /**
     * Ask before doing something that cannot be undone, or that throws away typing.
     *
     * One helper rather than a dialog per case: the cases differ only in wording, and the point of each is the
     * same — the user is about to lose something Cancel cannot get back.
     */
    confirm({ title, body, label = 'OK' }, onConfirm) {
        this.setState({ confirm: { title, body, label, onConfirm } });
    }

    request(path, method, { params, data } = {}) {
        const { appContext: { state: { idToken } } } = this.props;
        return Axios({
            url: Config.baseUrl + `${Config.pathPrefix}apricity-forecast/ml/${path}`,
            method,
            headers: { Authorization: idToken },
            ...(params ? { params } : {}),
            ...(data ? { data } : {})
        });
    }

    loadAlgorithm() {
        this.setState({ progress: <CircularProgress size={20} />, error: null });
        this.request('algorithm', 'GET', { params: { ref: this.ref } }).then(({ data }) => {
            // Nothing to load for a folder opened without a file — no manifest, no code.
            if (data.isFolder && !this.props.openFile) {
                return this.setState({ algorithm: data, progress: null });
            }
            this.setState({ algorithm: data, progress: null }, this.loadContent.bind(this));
        }).catch((error) => {
            this.setState({ progress: null, error: getErrorMessage(error) });
        });
    }

    loadContent() {
        this.setState({ progress: <CircularProgress size={20} />, error: null });
        this.request('algorithm/file', 'GET', { params: { ref: this.ref, file: this.file } })
            .then(({ data }) => {
                if (this.editingFile) {
                    this.setState({
                        body: data.body, original: data.body, actualFile: data.file || this.file,
                        progress: null, diff: ''
                    }, () => { this.loadHistory(); this.notifyDirty(); });
                    return;
                }
                const raw = JSON.parse(data.body || '{}');
                const form = {};
                FORM_KEYS.forEach((k) => { if (raw[k] !== undefined) form[k] = raw[k]; });
                if (form.opts === undefined) form.opts = [];
                if (form.dependencies === undefined) form.dependencies = [];
                const rows = (obj) => Object.keys(obj || {}).map((k) => ({ key: k, value: String(obj[k]) }));
                const deployInput = rows(raw.deploy && raw.deploy.input);
                const deployOutput = rows(raw.deploy && raw.deploy.output);
                delete form.deploy;         // held as rows, not in `form`
                const extraKeys = Object.keys(raw).filter((k) => !FORM_KEYS.includes(k));
                const advanced = extraKeys.length
                    ? JSON.stringify(extraKeys.reduce((o, k) => { o[k] = raw[k]; return o; }, {}), null, 4)
                    : '';
                this.setState({ raw, form, advanced, deployInput, deployOutput,
                    actualFile: data.file || this.file, progress: null, diff: '' }, () => {
                    // The baseline is what a save would produce RIGHT NOW, not the bytes that were read. So
                    // re-ordered keys or a defaulted empty list do not read as an unsaved change the moment
                    // the form opens.
                    this.setState({ original: JSON.stringify(this.manifestToSave(), null, 4) },
                        () => { this.loadHistory(); this.notifyDirty(); });
                });
            }).catch((error) => {
                this.setState({ progress: null, error: getErrorMessage(error) });
            });
    }

    loadHistory() {
        this.request('algorithm/history', 'GET', { params: { ref: this.ref, file: this.file } })
            .then(({ data }) => this.setState({ history: data.history || [] }))
            .catch((error) => this.setState({ error: getErrorMessage(error) }));
    }

    onSave() {
        const { note } = this.state;
        let body;
        if (this.editingFile) {
            body = this.state.body;
        } else {
            try {
                body = JSON.stringify(this.manifestToSave(), null, 4);
            } catch (err) {
                return this.setState({ error: `Advanced JSON is not valid: ${err.message}` });
            }
            // Refused here rather than at run time: the service rejects these too, but by then the failure is
            // a job that died on a Batch machine.
            const bad = (this.state.form.dependencies || []).filter((d) => d && !DEPENDENCY_RE.test(d.trim()));
            if (bad.length) {
                return this.setState({ error: `Not usable as pip requirements: ${bad.join(', ')}` });
            }
        }

        this.setState({ progress: <CircularProgress size={20} />, error: null });
        this.request('algorithm/file', 'POST', { data: { ref: this.ref, file: this.file, body, note } })
            .then(() => {
                // The note is cleared after use: carrying it to the next save would attribute an unrelated
                // change to the wrong reason, which is worse than no note at all.
                this.setState({ original: body, note: '', progress: null },
                    () => { this.loadHistory(); this.notifyDirty(); this.notifySaved(); });
            }).catch((error) => {
                this.setState({ progress: null, error: getErrorMessage(error) });
            });
    }

    /** The list shows the name and description from the manifest, so it has to hear about a save. */
    notifySaved() {
        if (!this.editingFile && this.props.onSaved) this.props.onSaved();
    }

    onRevert() {
        if (this.editingFile) {
            return this.setState({ body: this.state.original, diff: '' }, this.notifyDirty.bind(this));
        }
        this.setState({ diff: '' }, this.loadContent.bind(this));
    }

    onDeleteFile() {
        const file = this.file;
        this.confirm({
            title: `Delete ${file}?`,
            body: 'The file is removed from the algorithm. Its previous versions stay in the history, but '
                + 'nothing in this editor brings the file itself back.',
            label: 'Delete'
        }, () => {
            this.setState({ progress: <CircularProgress size={20} />, error: null });
            this.request('algorithm', 'DELETE', { params: { ref: this.ref, file } }).then(() => {
                // The file this editor was showing is gone, so there is nothing left for it to show — and the
                // tree still lists it until it is told.
                this.notifySaved();
                this.props.onClose();
            }).catch((error) => {
                this.setState({ progress: null, error: getErrorMessage(error) });
            });
        });
    }

    onRestore(version) {
        this.confirm({
            title: 'Restore this version?',
            body: `${this.file} is replaced with the version from `
                + `${Moment(version.changed).format(DATE_FORMAT)}${this.dirty
                    ? ', and the unsaved changes here are lost' : ''}. The current version stays in the `
                + 'history, so this can be undone by restoring it back.',
            label: 'Restore'
        }, () => {
            this.setState({ progress: <CircularProgress size={20} />, error: null });
            this.request('algorithm/rollback', 'POST', {
                data: { ref: this.ref, file: this.file, versionId: version.versionId,
                    note: `restored ${version.versionId}` }
            }).then(() => {
                this.setState({ progress: null }, () => { this.loadContent(); this.notifySaved(); });
            }).catch((error) => {
                this.setState({ progress: null, error: getErrorMessage(error) });
            });
        });
    }

    /**
     * Diff a past version against what is on screen.
     *
     * Against the editor rather than the stored latest, so it also answers "what am I about to change" — the
     * question that matters just before pressing Save.
     */
    onDiff(version) {
        this.setState({ progress: <CircularProgress size={20} />, error: null });
        this.request('algorithm/file', 'GET', {
            params: { ref: this.ref, file: this.file, versionId: version.versionId }
        }).then(({ data }) => {
            let current;
            try {
                current = this.editingFile ? this.state.body : JSON.stringify(this.manifestToSave(), null, 4);
            } catch (err) {
                current = '';
            }
            const patch = createTwoFilesPatch(
                `${this.file} (${Moment(version.changed).format(DATE_FORMAT)})`, `${this.file} (editor)`,
                data.body || '', current || '');
            this.setState({
                progress: null,
                diff: Diff2Html.getPrettyHtml(patch, {
                    inputFormat: 'diff', showFiles: false, matching: 'lines',
                    renderNothingWhenEmpty: true, outputFormat: 'line-by-line'
                })
            });
        }).catch((error) => {
            this.setState({ progress: null, error: getErrorMessage(error) });
        });
    }

    onSelectVersion(selected) {
        const { history } = this.state;
        if (!selected.length) return this.setState({ diff: '' });
        this.onDiff(history[selected[0]]);
    }

    onClose() {
        if (!this.dirty) return this.props.onClose();
        this.confirm({
            title: 'Close without saving?',
            body: `${this.editingFile ? this.file : 'The algorithm properties'} `
                + 'has changes that have not been saved.',
            label: 'Discard'
        }, () => this.props.onClose());
    }

    setForm(patch) {
        this.setState({ form: { ...this.state.form, ...patch } }, this.notifyDirty.bind(this));
    }

    setOpt(i, patch) {
        this.setForm({ opts: (this.state.form.opts || []).map((o, j) => (j === i ? { ...o, ...patch } : o)) });
    }

    // -----------------------------------------------------------------------------------------------------
    // Rendering
    // -----------------------------------------------------------------------------------------------------

    /** The manifest, as a form. */
    renderProperties() {
        const { algorithm, form, advanced } = this.state;
        const readOnly = this.readOnly;
        // The entry script has to be a file that exists — the service refuses a manifest naming one that does
        // not, and a dropdown of the real files makes that impossible to get wrong.
        const scripts = ((algorithm && algorithm.files) || []).filter((f) => !isManifestFile(f));
        const opts = form.opts || [];
        const deps = form.dependencies || [];
        const selectedFormats = Array.isArray(form.resultFormat) ? form.resultFormat : [];
        // A manifest may already name a format this build does not know — one added by a later release, or a
        // typo. Offering it alongside the known ones keeps it visible and removable; leaving it out would both
        // warn about an out-of-range value and quietly hide something that is really in the file.
        const formatChoices = [
            ...RESULT_FORMATS,
            ...selectedFormats
                .filter((v) => !RESULT_FORMATS.some((f) => f.id === v))
                .map((v) => ({ id: v, label: v, hint: 'not a format this build knows' }))
        ];

        return <div>
            <TextField style={{ width: 300, marginRight: 10 }} label="Name" value={form.name || ''}
                disabled={readOnly} helperText="Shown wherever an algorithm is picked"
                onChange={(e) => this.setForm({ name: e.target.value })} />
            <FormControl style={{ width: 240 }}>
                <InputLabel>Entry script</InputLabel>
                <Select value={scripts.includes(form.script) ? form.script : ''} disabled={readOnly}
                    onChange={(e) => this.setForm({ script: e.target.value })}>
                    {scripts.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                </Select>
            </FormControl>
            <FormControl style={{ width: 300, marginLeft: 10 }}>
                <InputLabel>Result formats</InputLabel>
                <Select multiple value={selectedFormats} disabled={readOnly}
                    renderValue={(sel) => sel.join(', ')}
                    onChange={(e) => this.setForm({ resultFormat: e.target.value })}>
                    {formatChoices.map((f) => <MenuItem key={f.id} value={f.id}>
                        <Checkbox style={{ padding: 4 }} checked={selectedFormats.indexOf(f.id) >= 0} />
                        <span>
                            {f.label}
                            <Typography variant="caption" style={{ display: 'block', opacity: 0.7 }}>
                                {f.hint}
                            </Typography>
                        </span>
                    </MenuItem>)}
                </Select>
                <FormHelperText>Which Sage views can draw this algorithm's results</FormHelperText>
            </FormControl>
            <TextField style={{ width: '100%', marginTop: 15 }} label="Description" multiline minRows={2}
                variant="outlined" value={form.description || ''} disabled={readOnly}
                onChange={(e) => this.setForm({ description: e.target.value })} />

            <Accordion defaultExpanded style={{ marginTop: 15 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Options ({opts.length})</AccordionSummary>
                <AccordionDetails>
                    <div style={{ width: '100%' }}>
                        <Typography variant="caption">
                            What a model can set when it runs this algorithm. The name is the key the Python
                            script reads out of its arguments.
                        </Typography>
                        {opts.map((opt, i) => <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                            <TextField style={{ width: 180, marginRight: 8 }} label="Name" value={opt.name || ''}
                                disabled={readOnly}
                                onChange={(e) => this.setOpt(i, { name: e.target.value })} />
                            <TextField style={{ flexGrow: 1, marginRight: 8 }} label="Description"
                                value={opt.description || ''} disabled={readOnly}
                                onChange={(e) => this.setOpt(i, { description: e.target.value })} />
                            {/*
                                Blank means "work it out from the default", which is how the seven built-ins get
                                proper checkboxes without a single manifest being edited — their flags all
                                default to "True" or "False". Setting it explicitly pins the type.
                            */}
                            <FormControl style={{ width: 110, marginRight: 8 }}>
                                <InputLabel>Type</InputLabel>
                                <Select value={opt.type || ''} disabled={readOnly}
                                    onChange={(e) => this.setOpt(i, { type: e.target.value || undefined })}>
                                    <MenuItem value=''><i>auto ({optionType(opt)})</i></MenuItem>
                                    {OPTION_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField style={{ width: 110, marginRight: 8 }} label="Default"
                                value={opt.defaultValue === undefined ? '' : String(opt.defaultValue)}
                                disabled={readOnly}
                                onChange={(e) => this.setOpt(i, { defaultValue: e.target.value })} />
                            <FormControlLabel control={<Checkbox checked={!!opt.required} disabled={readOnly}
                                onChange={(e) => this.setOpt(i, { required: e.target.checked })} />}
                                label="Required" />
                            <IconButton size="small" disabled={readOnly}
                                onClick={() => this.setForm({ opts: opts.filter((o, j) => j !== i) })}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </div>)}
                        {!readOnly && <Button startIcon={<AddIcon />}
                            onClick={() => this.setForm({ opts: [...opts, { name: '', description: '' }] })}>
                            Add option
                        </Button>}
                    </div>
                </AccordionDetails>
            </Accordion>

            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    Python packages ({deps.length})
                </AccordionSummary>
                <AccordionDetails>
                    <div style={{ width: '100%' }}>
                        <Typography variant="caption">
                            Installed with pip before the job runs. The image already carries the usual
                            scientific stack — this is for anything it does not.
                        </Typography>
                        {deps.map((dep, i) => <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                            <TextField style={{ width: 340 }} label="Requirement" value={dep} disabled={readOnly}
                                error={!!dep && !DEPENDENCY_RE.test(dep.trim())}
                                helperText="e.g. xgboost, or scikit-learn==1.4.2"
                                onChange={(e) => this.setForm({
                                    dependencies: deps.map((d, j) => (j === i ? e.target.value : d))
                                })} />
                            <IconButton size="small" disabled={readOnly}
                                onClick={() => this.setForm({
                                    dependencies: deps.filter((d, j) => j !== i)
                                })}><DeleteIcon fontSize="small" /></IconButton>
                        </div>)}
                        {!readOnly && <Button startIcon={<AddIcon />}
                            onClick={() => this.setForm({ dependencies: [...deps, ''] })}>Add package</Button>}
                    </div>
                </AccordionDetails>
            </Accordion>

            {this.renderDeployment()}

            {(advanced || !readOnly) && <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    Advanced{advanced ? '' : ' (empty)'}
                </AccordionSummary>
                <AccordionDetails>
                    <div style={{ width: '100%' }}>
                        <Typography variant="caption">
                            Manifest settings with no field of their own, as JSON — the <code>deploy</code> block
                            on the built-in algorithms is the usual one. Kept exactly as written.
                        </Typography>
                        <TextField style={{ width: '100%' }} multiline minRows={6} variant="outlined"
                            InputProps={{ style: CODE_STYLE, readOnly }}
                            value={advanced}
                            onChange={(e) => this.setState({ advanced: e.target.value },
                                this.notifyDirty.bind(this))} />
                    </div>
                </AccordionDetails>
            </Accordion>}
        </div>;
    }

    /**
     * The `deploy` block, as a form.
     *
     * What it is for: an ordinary run trains a model, stands a SageMaker end-point up to make predictions, and
     * tears the end-point down again. This block describes how to instead **keep** one standing, and what to
     * remember about it, so later runs can infer against it on new data without paying to retrain.
     *
     * The option name is a dropdown of the algorithm's actual options rather than free text, because that is
     * where the mistakes live: `knn`'s manifest names an output path (`pca_endpoint_name`) that its script never
     * sets — the script nests it under `pca_result` — so that half of its deployment silently resolves to
     * nothing. A free-text field cannot warn about that; a list of real options at least anchors one half.
     */
    renderDeployment() {
        const { form, deployInput, deployOutput } = this.state;
        const readOnly = this.readOnly;
        const optNames = (form.opts || []).map((o) => o.name).filter(Boolean);

        const table = (label, hint, rows, stateKey) => <div style={{ marginBottom: 15 }}>
            <Typography variant="subtitle2">{label}</Typography>
            <Typography variant="caption">{hint}</Typography>
            {rows.map((row, i) => <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                <FormControl style={{ width: 260, marginRight: 8 }}>
                    <InputLabel>Option</InputLabel>
                    <Select value={optNames.includes(row.key) ? row.key : ''} disabled={readOnly}
                        onChange={(e) => this.setRow(stateKey, i, { key: e.target.value })}>
                        {optNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </Select>
                    {!optNames.includes(row.key) && row.key &&
                        <span style={{ fontSize: 12, color: 'red' }}>{row.key} is not an option</span>}
                </FormControl>
                <TextField style={{ flexGrow: 1, marginRight: 8 }} label="Result path or literal"
                    value={row.value} disabled={readOnly}
                    onChange={(e) => this.setRow(stateKey, i, { value: e.target.value })} />
                <IconButton size="small" disabled={readOnly}
                    onClick={() => this.setState({
                        [stateKey]: rows.filter((r, j) => j !== i)
                    }, this.notifyDirty.bind(this))}><DeleteIcon fontSize="small" /></IconButton>
            </div>)}
            {!readOnly && <Button startIcon={<AddIcon />} onClick={() => this.setState({
                [stateKey]: [...rows, { key: '', value: '' }]
            }, this.notifyDirty.bind(this))}>Add row</Button>}
        </div>;

        return <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                Deployment ({deployInput.length + deployOutput.length})
            </AccordionSummary>
            <AccordionDetails>
                <div style={{ width: '100%' }}>
                    <Typography variant="caption" component="div" style={{ marginBottom: 10 }}>
                        An ordinary run trains, predicts, and shuts its end-point down. This describes how to
                        keep one standing instead — so a later run can infer on new data without retraining.
                        A value is either a path into the previous run's result
                        (<code>pca_result.pca_training_job_name</code>) or a literal
                        (<code>True</code>, <code>False</code>).
                    </Typography>
                    {table('Redeploy inputs',
                        'Options for a run that rebuilds the end-point from an already-trained model — feed the '
                        + 'training job names back, and set deploy True / train False.',
                        deployInput, 'deployInput')}
                    {table('Deployment outputs',
                        'What to remember from that run: the end-point names. These are also exactly what gets '
                        + 'fed back in to re-use the deployment later, which is why each one must name a real '
                        + 'option.',
                        deployOutput, 'deployOutput')}
                </div>
            </AccordionDetails>
        </Accordion>;
    }

    setRow(stateKey, i, patch) {
        this.setState({
            [stateKey]: this.state[stateKey].map((r, j) => (j === i ? { ...r, ...patch } : r))
        }, this.notifyDirty.bind(this));
    }

    /**
     * The file editor, with syntax highlighting.
     *
     * `react-simple-code-editor` is a transparent `<textarea>` laid exactly over a highlighted `<pre>`, which
     * is what lets it stay a normal editable field. The two elements must agree on every text-metric property
     * or the caret drifts away from the glyphs, so the component sets `white-space: pre-wrap` on both and this
     * does not fight it — long lines now wrap instead of scrolling sideways as the plain field did. That is the
     * one behavioural change, and it is the safe side of the trade: a caret that lands in the wrong place is a
     * far worse bug than a wrapped comment.
     */
    renderCode() {
        return <Editor
            value={this.state.body}
            onValueChange={(body) => this.setState({ body }, this.notifyDirty.bind(this))}
            highlight={(code) => highlightCode(code, this.file)}
            readOnly={this.readOnly}
            padding={12}
            tabSize={4}
            insertSpaces={true}
            textareaId={`algorithm-file-${this.file}`}
            style={CODE_EDITOR_STYLE} />;
    }

    render() {
        const { algorithm, form, note, history, diff, confirm, progress, error } = this.state;
        const readOnly = this.readOnly;
        const dirty = this.dirty;
        const ready = algorithm && (this.editingFile || form);

        const versionColumns = [
            { id: 'changed', label: 'Changed', formatValue: (v) => (v ? Moment(v).format(DATE_FORMAT) : '') },
            // A cognito:username is a UUID, which tells a reader nothing. `User` resolves it to a name or
            // email and caches per id, and it falls back to showing the raw value when there is no such user —
            // which is right for the non-human authors that appear here, like the manifest migration.
            {
                id: 'author',
                label: 'By',
                formatValue: (v) => (v ? <User appContext={this.props.appContext} userId={v} /> : '')
            },
            { id: 'note', label: 'Note' },
            { id: 'size', label: 'Size' },
            {
                id: 'latest',
                label: '',
                formatValue: (v, row) => (v || !row || row.deleted ? '' :
                    <Tooltip title="Restore this version">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); this.onRestore(row); }}>
                            <RestoreIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>)
            }
        ];

        return <div style={{ width: 1000, margin: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" style={{ flexGrow: 1 }}>
                    {(algorithm && algorithm.name) || this.ref}
                    <span style={{ fontSize: 13, marginLeft: 10, opacity: 0.7 }}>
                        {this.editingFile ? this.file : 'properties'}{readOnly ? ' · read only' : ''}
                    </span>
                </Typography>
                {progress}
                <IconButton onClick={this.onClose.bind(this)}><CloseIcon /></IconButton>
            </div>
            <Typography variant="caption">{this.ref}</Typography>
            {error && <div style={{ color: 'red', margin: 5 }}>{error}</div>}

            <div style={{ display: 'flex', alignItems: 'flex-end', margin: '10px 0' }}>
                {!readOnly && <TextField style={{ flexGrow: 1, marginRight: 10 }} label="Why this change"
                    value={note} onChange={(e) => this.setState({ note: e.target.value })} />}
                {!readOnly && <Button color="primary" disabled={!dirty}
                    onClick={this.onSave.bind(this)}>Save</Button>}
                {!readOnly && <Button disabled={!dirty} onClick={this.onRevert.bind(this)}>Revert</Button>}
                {/* Only a real file can be deleted here. The manifest goes with the algorithm, not on its own. */}
                {!readOnly && this.editingFile && <Tooltip title="Delete this file">
                    <IconButton onClick={this.onDeleteFile.bind(this)}><DeleteIcon /></IconButton>
                </Tooltip>}
            </div>

            {this.isFolder && !this.editingFile
                ? <div style={{ opacity: 0.7, margin: 20 }}>
                    A folder of shared files — pick one from the tree to edit it. There is nothing to run here,
                    so it has no properties.
                </div>
                : (ready
                    ? (this.editingFile ? this.renderCode() : this.renderProperties())
                    : <div style={{ opacity: 0.6, margin: 20 }}>loading…</div>)}

            <Accordion style={{ marginTop: 10 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    History — {this.state.actualFile || this.file} ({history.length})
                </AccordionSummary>
                <AccordionDetails>
                    <div style={{ width: '100%' }}>
                        <EnhancedTable
                            disableMultiSelect={true}
                            disableActions={true}
                            rowsPerPage={10}
                            orderBy="changed"
                            // Newest first. The store already returns them that way, but EnhancedTable sorts
                            // client-side and defaults to ascending, which buried the latest change at the
                            // bottom — the opposite of what a history is read for.
                            order="desc"
                            columnData={versionColumns}
                            data={history}
                            onSelected={this.onSelectVersion.bind(this)}
                            title="Versions" />
                        {diff
                            ? <div style={{ marginTop: 10 }} dangerouslySetInnerHTML={{ __html: diff }} />
                            : <div style={{ marginTop: 10, opacity: 0.7 }}>
                                Select a version to compare it with what is on screen.
                            </div>}
                    </div>
                </AccordionDetails>
            </Accordion>

            {confirm && <Dialog open={true}>
                <DialogTitle>{confirm.title}</DialogTitle>
                <DialogContent><Typography>{confirm.body}</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => this.setState({ confirm: null })}>Cancel</Button>
                    <Button color="secondary" onClick={() => {
                        const { onConfirm } = confirm;
                        this.setState({ confirm: null }, onConfirm);
                    }}>{confirm.label}</Button>
                </DialogActions>
            </Dialog>}
        </div>;
    }
}

export default MLAlgorithmView;
